import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { buildViewPdfQueryVariables } from '../../entry-points/viewPdfMacroParamsUtils.ts';

import type {
	useViewPdfDataQuery$data,
	useViewPdfDataQuery$variables,
} from './__generated__/useViewPdfDataQuery.graphql.ts';

type ContentEdge = NonNullable<NonNullable<useViewPdfDataQuery$data['content']>['edges']>[number];
type AttachmentNode = NonNullable<
	NonNullable<NonNullable<NonNullable<ContentEdge>['node']>['attachments']>['nodes']
>[number];
type AttachmentExtension = NonNullable<NonNullable<AttachmentNode>['extensions']>[number];

export type ViewPdfDataResult = {
	/** Media file ID (fileStoreId) to pass to DocumentViewer via mediaClient */
	mediaFileId: string | null;
	/** Confluence media collection name, e.g. "contentId-{pageId}" */
	collectionName: string | null;
	/** Content id used to scope Media client auth for file access */
	sourceContentId: string | null;
	isLoading: boolean;
	error: Error | null;
};

export type UseViewPdfDataOptions = {
	/** Content ID of the page the macro is on (always required). */
	contentId: string | null;
	/** File name of the PDF attachment to embed (required). */
	attachmentName: string | null;
	/**
	 * Optional: title of the page containing the PDF attachment.
	 * When provided, the attachment is looked up on this page instead of the
	 * current page (cross-page attachment). Must be combined with spaceKey.
	 */
	pageName?: string | null;
	/**
	 * Optional: space key of the page containing the PDF attachment.
	 * Required when pageName is provided for cross-page resolution.
	 */
	spaceKey?: string | null;
	/**
	 * Optional: date of the blog post in mm/dd/yyyy format.
	 * When provided together with pageName and spaceKey, the content type
	 * is treated as a blog post instead of a regular page.
	 */
	date?: string | null;
};

/**
 * Hook that fetches the media file ID and collection name for a given PDF
 * attachment via the AGG GraphQL `content → attachments` API, for use with
 * the Platform Media DocumentViewer component (@atlaskit/media-document-viewer).
 *
 * This uses the existing `content.attachments` field (same pattern as
 * `attachments-page`) to avoid waiting for a
 * dedicated `viewpdfMacro` AGG field. The attachment is matched client-side
 * by title === attachmentName.
 *
 * Supports both same-page and cross-page (page/spaceKey/date params) attachment
 * resolution using the AGG `content` query's spaceKey, title, type, and
 * postingDay arguments.
 *
 * Known limitations:
 * - No server-side filename filtering; all attachments (up to 250) are fetched
 *   and filtered client-side.
 */
export const useViewPdfData = ({
	contentId,
	attachmentName,
	pageName,
	spaceKey,
	date,
}: UseViewPdfDataOptions): ViewPdfDataResult => {
	const normalizedPageName = pageName?.trim() || null;

	// Cross-page mode: look up by title (+ optionally spaceKey and postingDay for blogs).
	// Same-page mode: look up by contentId.
	const isCrossPage = Boolean(normalizedPageName);

	// Skip only when core required params are missing.
	// We intentionally allow cross-page lookups without spaceKey to preserve legacy behavior.
	const skip = !contentId || !attachmentName;
	const variables: useViewPdfDataQuery$variables = buildViewPdfQueryVariables({
		contentId,
		pageName: normalizedPageName,
		spaceKey,
		date,
	});

	const { data, loading, error } = useQueryInterop<
		useViewPdfDataQuery$data,
		useViewPdfDataQuery$variables
	>(
		graphql`
			query useViewPdfDataQuery(
				$contentId: ID
				$contentStatus: [String]
				$spaceKey: String
				$title: String
				$type: String
				$postingDay: String
			) {
				content(
					id: $contentId
					status: $contentStatus
					spaceKey: $spaceKey
					title: $title
					type: $type
					postingDay: $postingDay
				) {
					edges {
						node {
							id
							attachments(first: 250) {
								nodes {
									title
									extensions {
										key
										value
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			variables,
			skip,
			fetchPolicy: 'cache-first',
		},
	);

	// When required params are missing, return empty result (no error).
	if (skip) {
		return {
			mediaFileId: null,
			collectionName: null,
			sourceContentId: null,
			isLoading: false,
			error: null,
		};
	}

	if (error) {
		return {
			mediaFileId: null,
			collectionName: null,
			sourceContentId: null,
			isLoading: false,
			error,
		};
	}

	// Find the attachment whose title matches the requested attachment name.
	// Use case-insensitive matching to preserve legacy backend parity:
	// AttachmentManager.getAttachment() performs case-insensitive lookup.
	const contentNode = data?.content?.edges?.[0]?.node;
	const attachmentNodes = contentNode?.attachments?.nodes ?? [];
	const matchingAttachment = attachmentNodes.find(
		(node: AttachmentNode) => node?.title?.toLowerCase() === attachmentName?.toLowerCase(),
	);

	// Extract the media fileId from the attachment's extensions array.
	// The 'fileId' extension key is the Media API file ID (same as fileStoreId).
	const mediaFileId =
		matchingAttachment?.extensions?.find((ext: AttachmentExtension) => ext?.key === 'fileId')
			?.value ?? null;

	// collectionName is "contentId-{pageId}".
	// For cross-page, use the resolved page's id from the query response.
	// For same-page, use the current contentId.
	const resolvedContentId = isCrossPage ? (contentNode?.id ?? null) : contentId;
	const collectionName = resolvedContentId ? `contentId-${resolvedContentId}` : null;

	return {
		mediaFileId,
		collectionName,
		sourceContentId: resolvedContentId,
		isLoading: loading && !data,
		error: null,
	};
};
